/**
 * Dupliquer une entree (« Clone » de Bitwarden et de KeePassXC, « Duplicate »
 * de 1Password) : un second compte sur le meme site, une variante de carte ou
 * d'identite, sans tout ressaisir. La copie reprend les secrets de l'entree --
 * identifiant, mot de passe, TOTP, sites, notes, champs, carte/identite -- et
 * en ecarte ce qui n'appartient qu'a l'original :
 *   - ses passkeys : une passkey est UNE cle enregistree aupres du site (un seul
 *     credentialId, une seule cle publique cote serveur) ; la dupliquer ferait
 *     deux entrees qui pretendent au meme identifiant (Bitwarden l'ecarte aussi) ;
 *   - l'historique de ses mots de passe et ses versions : c'est le passe de l'AUTRE compte ;
 *   - son etat d'archive et de corbeille : la copie nait active ;
 *   - son exclusion de la Sante du coffre : un autre compte n'a pas ete juge,
 *     la copie nait surveillee.
 * La re-demande du mot de passe maitre, elle, est TOUJOURS conservee : une
 * copie qui la perdrait ouvrirait sans controle ce que l'original protege.
 *
 * Module pur, zero E/S : `VaultStore.duplicateEntry` le compose avec un
 * nouvel identifiant et un nouveau chiffrement (nonce neuf, AAD de la copie).
 */
import type { EntrySecrets } from '@klavox/crypto-core';

/** Suffixe ajoute au nom de la copie, pour la distinguer de l'original dans la liste. */
export const CLONE_NAME_SUFFIX = ' (copie)';

/** Cles propres a l'original, jamais reprises par la copie. */
const ORIGINAL_ONLY_KEYS = ['passkeys', 'passwordHistory', 'itemHistory', 'archived', 'trashedAt', 'healthExcluded', 'healthIgnored', 'passkeyElsewhere'] as const;

/** Nom de la copie : celui de l'original suivi de « (copie) ». */
export function cloneName(name: string | undefined): string {
  const base = (name ?? '').trim() || 'Sans nom';
  return base + CLONE_NAME_SUFFIX;
}

/** Secrets de la copie : tout l'original, moins ce qui n'appartient qu'a lui ; nom suffixe. */
export function cloneSecrets(secrets: EntrySecrets): EntrySecrets {
  const copy: Record<string, unknown> = { ...secrets };
  for (const key of ORIGINAL_ONLY_KEYS) delete copy[key];
  return { ...copy, name: cloneName(secrets.name) } as EntrySecrets;
}
